import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { createAxiosThunk } from 'utils/reduxUtil'
import api from 'api/axios/foka'
import { FILE_COUNT_LIMIT } from 'features/bulk/upload/UploadModal'
import { TEMPLATE_TYPE } from '../BulkCodes'

const createUpload = createAxiosThunk(
  'bulk/createUpload',
  (file, { getState }) => {
    const { templateType } = getState().bulk.uploadModal
    const { name, filePath } = file
    return api.bulk.postUpload({
      domainType: TEMPLATE_TYPE[templateType].domainType.name,
      methodType: TEMPLATE_TYPE[templateType].methodType.name,
      filePath,
      name,
    })
  }
)

export const createUploads = createAsyncThunk(
  'bulk/createUploads',
  async (noArgs, { getState, dispatch }) => {
    const { files } = getState().bulk.uploadModal
    const results = await Promise.all(
      files.map(file => dispatch(createUpload(file)))
    )
    return results
      .filter(({ error }) => error)
      .map(
        ({
          payload: { errorCode, errorMessage },
          meta: {
            arg: { name },
          },
        }) => ({ errorCode, errorMessage, name })
      )
      .reduce((carry, { errorCode, errorMessage, name }) => {
        if (!Object.prototype.hasOwnProperty.call(carry, errorCode)) {
          carry[errorCode] = {
            name: errorCode,
            message: errorMessage,
            fileNames: [],
          }
        }
        carry[errorCode].fileNames.push(name)
        return carry
      }, {})
  }
)

export const uploadFile = createAxiosThunk('bulk/uploadFile', async file => {
  const {
    data: {
      https: { path },
      property: { filename },
    },
  } = await api.bulk.uploadFile(file)
  return api.response({
    filePath: path,
    name: filename,
  })
})

export const uploadFiles = createAxiosThunk(
  'bulk/uploadFiles',
  (files, { dispatch }) =>
    Promise.all(files.map(file => dispatch(uploadFile(file))))
)

export const fetchTemplateFile = createAxiosThunk(
  'bulk/fetchTemplateFile',
  (noArgs, { getState }) => {
    const { templateType } = getState().bulk.uploadModal
    return api.bulk.getTemplateFile(templateType)
  }
)

const initialState = {
  isShow: false,
  isLoading: false,
  uploading: false,
  disabled: false,
  templateType: null,
  templateFileUrl: null,
  templateLoading: false,
  files: [],
}

const uploadModalSlice = createSlice({
  name: 'bulk/uploadModal',
  initialState,

  reducers: {
    showUploadModal: state => {
      state.isShow = true
    },
    hideUploadModal: () => initialState,
    setTemplateType: (state, { payload }) => {
      state.templateType = payload
    },
    addFile: (state, { payload }) => {
      state.files.push(payload)
    },
    removeFile: (state, { payload }) => {
      const index = state.files.findIndex(
        ({ filePath }) => filePath === payload
      )
      if (index !== -1) state.files.splice(index, 1)
    },
  },

  extraReducers: builder => {
    builder.addCase(createUpload.pending, state => {
      state.disabled = true
    })

    builder.addCase(createUpload.fulfilled, state => {
      state.disabled = false
    })

    builder.addCase(createUpload.rejected, state => {
      state.disabled = false
    })

    builder.addCase(uploadFile.fulfilled, (state, { payload }) => {
      if (state.files.length < FILE_COUNT_LIMIT) {
        state.files.push(payload)
      }
    })

    builder.addCase(uploadFiles.pending, state => {
      state.uploading = true
    })

    builder.addCase(uploadFiles.fulfilled, state => {
      state.uploading = false
    })

    builder.addCase(uploadFiles.rejected, state => {
      state.uploading = false
    })

    builder.addCase(fetchTemplateFile.pending, state => {
      state.templateLoading = true
    })

    builder.addCase(fetchTemplateFile.fulfilled, (state, { payload }) => {
      state.templateFileUrl = `${payload.downloadUrl}?download`
      state.templateLoading = false
    })

    builder.addCase(fetchTemplateFile.rejected, state => {
      state.templateLoading = false
    })
  },
})

const { actions, reducer } = uploadModalSlice
export const {
  showUploadModal,
  hideUploadModal,
  setTemplateType,
  addFile,
  removeFile,
} = actions
export default reducer
